import { Button, Dialog, Icon, Sheet, useIsMobile } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { usePageHeader } from '../page-header';
import { Breadcrumb, type Crumb } from '../breadcrumb';
import {
  LineItemDetail,
  type LineItemDetailData,
  type LineItemDetailHandlers,
} from './line-item-detail';
import { lineItemContractLabel } from './contract-label';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C25 · The line item detail in three frames. `task-surface.tsx`'s shape.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Every number is measured out of `2fazis.markup.html` (3294–3320) rather than chosen, and all three match the
 * task surface exactly — which is the point rather than a coincidence: two details that open
 * differently teach two habits.
 *
 *  * **drawer** — a `Sheet` at `min(560px, 96vw)`, narrow layout (3294–3299);
 *  * **modal** — a bare `Dialog` at 1060px with its own 48px header, wide layout (3301–3320);
 *  * **full page** — a route, `max-w-[1160px]`, wide layout (1552–1559).
 *
 * ⚠️ **The mode lives with the caller**, so a deep link can open straight into `full` and a row
 * click opens the drawer.
 */
export type LineItemMode = 'drawer' | 'modal' | 'full';

export interface LineItemSurfaceProps {
  readonly mode: LineItemMode;
  readonly open: boolean;
  readonly data: LineItemDetailData;
  readonly handlers: LineItemDetailHandlers;
  readonly onModeChange: (mode: LineItemMode) => void;
  readonly onClose: () => void;
  /** ⚠️ Workspace-relative, for the breadcrumb — R-38. */
  readonly onNavigate?: (path: string) => void;
}

export function LineItemSurface(props: LineItemSurfaceProps) {
  const mobile = useIsMobile();
  if (props.mode === 'full') return <FullPage {...props} />;
  /* ⚠️ 17 Oct 2026 · on a phone a modal is the full-screen drawer — the product owner. */
  if (props.mode === 'modal' && !mobile) return <Modal {...props} />;
  return <Drawer {...props} />;
}

/**
 * ⚠️⚠️ **THE TRAIL, ONCE, FOR ALL THREE FRAMES — item 2, second pass, 15 Sep.** It was written into
 * the full page's header and nowhere else, so the drawer and the modal still said `CODE · name` —
 * the same drift `task-surface.tsx` records: one body behind a `wide` flag, and three chromes that
 * grew apart within a day. A function rather than markup, so there is one place where *where am I*
 * is decided.
 */
function lineItemCrumbs(
  item: LineItemSurfaceProps['data']['item'],
  t: Translate,
  onNavigate?: (path: string) => void,
): Crumb[] {
  return [
    { label: t('breadcrumb.home'), onClick: () => onNavigate?.('/home') },
    { label: t('title.projects'), onClick: () => onNavigate?.('/projects') },
    {
      /* ⚠️ C56 · the crumb is dropped below when there is no project, so these are safe. */
      label: item.projectName ?? '',
      onClick: () => onNavigate?.(`/projects/${item.projectId ?? ''}`),
    },
    {
      label: lineItemContractLabel(item),
      /* R-57 · the contract's address is its identifier, which every contract has. */
      onClick: () => onNavigate?.(`/contracts/${lineItemContractLabel(item)}`),
    },
    { label: item.name },
  ];
}

function Drawer({ open, data, handlers, onModeChange, onClose, onNavigate }: LineItemSurfaceProps) {
  const t = useT();

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      /* ⚠️ The trail IS the title — item 2. See `contract-surface.tsx`. */
      title={<Breadcrumb crumbs={lineItemCrumbs(data.item, t, onNavigate)} />}
      width="min(560px, 96vw)"
      closeLabel={t('common.close')}
      /* 17 Oct 2026 · no mode buttons on a phone: the drawer is the only frame there. */
      actions={
        useIsMobile()
          ? []
          : [
              {
                label: t('task.mode.modal'),
                icon: 'maximize',
                onSelect: () => {
                  onModeChange('modal');
                },
              },
            ]
      }
    >
      <LineItemDetail data={data} handlers={handlers} wide={false} />
    </Sheet>
  );
}

/**
 * ⚠️ **`bare`, with a header this file draws** — the mockup's own 48px strip carrying the code, the
 * name and the three mode controls. A `Dialog`'s own header gives a title and a close button and
 * nowhere to put minimise and maximise.
 */
function Modal({ open, data, handlers, onModeChange, onClose, onNavigate }: LineItemSurfaceProps) {
  const t = useT();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      bare
      /**
       * ⚠️⚠️ **WIDER SINCE ITEM 36 — it was a flat 1060px and the line-item table scrolled inside
       * it.** *"The contract's line items scroll horizontally today"* is the feedback verbatim: a
       * table of eight columns in a 1060px box on a 1920px screen, with the window's remaining
       * 860 pixels showing the scrim. `min()` takes the window when the window is smaller.
       */
      width="min(1320px, 94vw)"
      closeLabel={t('common.close')}
    >
      <div className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
        {/* ⚠️ The same trail the page and the drawer draw. */}
        <Breadcrumb
          copyValue={data.item.identifier}
          crumbs={lineItemCrumbs(data.item, t, onNavigate)}
        />

        <div className="flex-1" />

        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.drawer')}
          title={t('task.mode.drawer')}
          onClick={() => {
            onModeChange('drawer');
          }}
        >
          <Icon name="minimize" size={15} />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.full')}
          title={t('task.mode.full')}
          onClick={() => {
            onModeChange('full');
          }}
        >
          <Icon name="maximize" size={15} />
        </Button>
        <Button variant="ghost" size="icon-sm" aria-label={t('common.close')} onClick={onClose}>
          <Icon name="x" size={15} />
        </Button>
      </div>

      {/* ⚠️ A flex column the detail FILLS rather than the scroller — 14 Oct. The detail's header
          stays put and its two halves scroll apart (`DetailFrame` in `detail-chrome.tsx`); the
          overflow is only the fallback for a body that is not bounded. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <LineItemDetail data={data} handlers={handlers} wide />
      </div>
    </Dialog>
  );
}

/**
 * ⚠️ **The full page claims the shell's header** — `task-surface.tsx` records what happens
 * otherwise: two headers ten pixels apart, where the mockup has one.
 */
function FullPage({ data, handlers, onModeChange, onClose, onNavigate }: LineItemSurfaceProps) {
  const t = useT();

  usePageHeader(
    () => (
      <>
        <Button
          variant="ghost"
          size="icon-sm"
          className="-ml-1.5"
          aria-label={t('task.action.back')}
          onClick={onClose}
        >
          <Icon name="chevron-left" size={16} />
        </Button>
        {/**
         * ⚠️ The trail replaces `CODE · name` — 14 Sep, as on the task and the contract.
         *
         * ⚠️ **The full trail, now that `lineItemSchema` carries `projectName`.** It did not on
         * 14 Sep — the wire derived the project's id through the contract and stopped — so this
         * skipped the project rather than printing a uuid. The name comes off the same join.
         *
         * ⚠️ **`HSO-C01/2` is the quotable identifier**, matching the address (P2-9) rather than
         * the bare ordinal, which means nothing on its own.
         */}
        <Breadcrumb
          copyValue={data.item.identifier}
          crumbs={lineItemCrumbs(data.item, t, onNavigate)}
        />

        <div className="flex-1" />

        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.drawer')}
          title={t('task.mode.drawer')}
          onClick={() => {
            onModeChange('drawer');
          }}
        >
          <Icon name="minimize" size={15} />
        </Button>
      </>
    ),
    /* ⚠⚠️ **THE VALUES IT RENDERS, AND NOT THE CALLBACKS — WHICH COST A DEBUGGING SESSION.**
       `onClose` and `onModeChange` are inline arrows at the call site, so they have a new identity
       every render; listing them re-ran the effect, which set state on the PROVIDER, which
       re-rendered this screen, which made new arrows. 2,056 renders, and the visible symptom was
       not a hang: the address changed to `/contracts/HSO-C01` and the LIST stayed on screen,
       because the transition never settled. `usePageHeader`'s own header describes this exact loop
       and holds `build` in a ref so the closures stay current without being dependencies. */
    [data.item.contractIdentifier, data.item.identifier, data.item.name, data.item.projectName],
  );

  return (
    /* ⚠️ No `data-screen` here — the ROUTE carries it, because the page has to be marked even
       when the contract is unknown. See `contracts-route.tsx`. */
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="mx-auto flex min-h-0 w-full max-w-[1160px] flex-1 flex-col">
        <LineItemDetail data={data} handlers={handlers} wide />
      </div>
    </div>
  );
}
